import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  ListGroup,
  Pagination,
  Container,
} from 'react-bootstrap';
import { useGetSalesQuery, useUpdateSaleMutation } from '../../slices/salesApiSlice';
import { useGetProductsQuery } from '../../slices/productsApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';

const SalesSearchReplaceScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [replacementName, setReplacementName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [previewResults, setPreviewResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [focusedBox, setFocusedBox] = useState('search');

  const { data: salesData, isLoading, error, refetch } = useGetSalesQuery();
  const {
    data: productsData = { products: [], pages: 1 },
    isLoading: loadingProducts,
    error: errorProducts,
  } = useGetProductsQuery({ pageNumber });

  const [updateSale] = useUpdateSaleMutation();

  const handleProductSelect = (product) => {
    if (focusedBox === 'search') {
      setSearchTerm(product.name);
    } else {
      setReplacementName(product.name);
      setSelectedProduct(product);
    }
    setPreviewResults([]);
    setSelectedItems({});
    setSelectAll(false);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const matches = salesData?.sales?.flatMap((sale) =>
      sale.saleItems
        .filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((item) => ({
          saleId: sale._id,
          itemId: item._id,
          originalName: item.name,
          qty: item.qty,
          price: item.price,
          date: sale.date,
        }))
    );

    setPreviewResults(matches || []);
    setSelectedItems({});
    setSelectAll(false);
  };

  const toggleSelectAll = () => {
    const newState = !selectAll;
    setSelectAll(newState);

    if (newState) {
      const all = {};
      previewResults.forEach((item) => {
        all[item.itemId] = true;
      });
      setSelectedItems(all);
    } else {
      setSelectedItems({});
    }
  };

  const toggleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      const updated = { ...prev };
      if (updated[itemId]) delete updated[itemId];
      else updated[itemId] = true;
      return updated;
    });
  };

  const handleReplace = async () => {
    if (!replacementName.trim()) return toast.error('Replacement name required');
    if (!searchTerm.trim()) return toast.error('Search term required');

    const replacementProduct =
      productsData.products.find(
        (p) => p.name.toLowerCase() === replacementName.toLowerCase()
      ) ||
      productsData.products.find((p) =>
        p.name.toLowerCase().includes(replacementName.toLowerCase())
      );

    if (!replacementProduct) {
      return toast.error(`Product "${replacementName}" not found`);
    }

    const selectedIds = Object.keys(selectedItems);
    if (selectedIds.length === 0) {
      return toast.info('No items selected for replacement');
    }

    setIsReplacing(true);

    try {
      const groupedBySale = {};

      previewResults.forEach((item) => {
        if (selectedItems[item.itemId]) {
          if (!groupedBySale[item.saleId]) groupedBySale[item.saleId] = [];
          groupedBySale[item.saleId].push(item.itemId);
        }
      });

      for (const sale of salesData.sales) {
        if (!groupedBySale[sale._id]) continue;

        // Only change product (controller will re‑fetch Product and overwrite name/price/image)
        const updatedItems = sale.saleItems.map((item) =>
          groupedBySale[sale._id].includes(item._id)
            ? {
                ...item,
                product: replacementProduct._id,
              }
            : item
        );

        // IMPORTANT: match your slice (body = data) AND controller (expects flat fields)
        await updateSale({
          saleId: sale._id,
          saleItems: updatedItems,
          supplierDetails: sale.supplierDetails,
          note: sale.note,
          status: sale.status,
          date: sale.date,
          expiryDate: sale.expiryDate,
          images: sale.images,
        }).unwrap();
      }

      toast.success(`Replaced selected items with "${replacementProduct.name}"`);
      setPreviewResults([]);
      setSelectedItems({});
      setSelectAll(false);
      setSearchTerm('');
      setReplacementName('');
      setSelectedProduct(null);

      await refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed');
    } finally {
      setIsReplacing(false);
    }
  };

  const activeFilter = focusedBox === 'search' ? searchTerm : replacementName;
  const filteredProducts = productsData.products.filter((p) =>
    p.name.toLowerCase().includes(activeFilter.toLowerCase())
  );

  return (
    <Container fluid>
      <Row>
        <Col md={3}>
          <h5 className="mb-3">Product List</h5>
          {loadingProducts ? (
            <Loader />
          ) : errorProducts ? (
            <Message variant="danger">
              {errorProducts?.data?.message || 'Failed to load products'}
            </Message>
          ) : (
            <>
              <ListGroup>
                {filteredProducts.map((product) => (
                  <ListGroup.Item
                    key={product._id}
                    action
                    onClick={() => handleProductSelect(product)}
                  >
                    {product.name} — D{product.price?.toFixed(2)}
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <Pagination className="mt-3">
                {[...Array(productsData.pages).keys()].map((x) => (
                  <Pagination.Item
                    key={x + 1}
                    active={x + 1 === pageNumber}
                    onClick={() => setPageNumber(x + 1)}
                  >
                    {x + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </>
          )}
        </Col>

        <Col md={9}>
          <Row className="mb-3">
            <Col><h1>Search & Replace Sale Product Name</h1></Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Search for product name"
                value={searchTerm}
                onFocus={() => setFocusedBox('search')}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Replacement name"
                value={replacementName}
                onFocus={() => setFocusedBox('replace')}
                onChange={(e) => {
                  setReplacementName(e.target.value);
                  setSelectedProduct(null);
                }}
              />
            </Col>
            <Col md={4}>
              <Button
                variant="primary"
                onClick={handleSearch}
                className="me-2"
                disabled={!searchTerm.trim() || isReplacing}
              >
                Preview Matches
              </Button>
              <Button
                variant="success"
                onClick={handleReplace}
                disabled={!replacementName.trim() || isReplacing}
              >
                Replace Selected
              </Button>
            </Col>
          </Row>

          {selectedProduct && (
            <Alert variant="info">
              Selected replacement: <strong>{selectedProduct.name}</strong> — D
              {selectedProduct.price.toFixed(2)}
            </Alert>
          )}

          {isLoading ? (
            <Loader />
          ) : error ? (
            <Message variant="danger">{error?.data?.message || 'Error loading sales'}</Message>
          ) : previewResults.length === 0 ? (
            <Alert variant="info">No matching items found.</Alert>
          ) : (
            <>
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={selectAll}
                onChange={toggleSelectAll}
                className="mb-2"
              />

              <Table striped bordered hover responsive className="table-sm">
                <thead>
                  <tr>
                    <th></th>
                    <th>DATE</th>
                    <th>ORIGINAL NAME</th>
                    <th>QTY</th>
                    <th>PRICE</th>
                  </tr>
                </thead>
                <tbody>
                  {previewResults.map((item) => (
                    <tr key={item.itemId}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={!!selectedItems[item.itemId]}
                          onChange={() => toggleSelectItem(item.itemId)}
                        />
                      </td>
                      <td>{new Date(item.date).toLocaleDateString()}</td>
                      <td>{item.originalName}</td>
                      <td>{item.qty}</td>
                      <td>D{item.price.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SalesSearchReplaceScreen;
